import React from "react";
import { X, MapPin } from "lucide-react";
import { SiteGroup } from "../../types/organisation";
import {
  useGetSocietes,
  useGetBUs,
  useGetActivites,
  useGetFilieres,
  useGetRegions,
} from "../../hooks";

interface AddSiteGroupDialogProps {
  isOpen: boolean;
  newGroup: SiteGroup;
  setNewGroup: (g: SiteGroup) => void;
  onAddGroup: () => void;
  onClose: () => void;
}

export function AddSiteGroupDialog({
  isOpen,
  newGroup,
  setNewGroup,
  onAddGroup,
  onClose,
}: AddSiteGroupDialogProps) {
  const { data: societes = [] } = useGetSocietes();
  const { data: bus = [] } = useGetBUs();
  const { data: activites = [] } = useGetActivites();
  const { data: filieres = [] } = useGetFilieres();
  const { data: regions = [] } = useGetRegions();

  if (!isOpen) return null;

  const inputStyle: React.CSSProperties = {
    width: "100%",
    padding: "10px 14px",
    border: "2px solid #e2e8f0",
    borderRadius: "10px",
    fontSize: "14px",
    outline: "none",
    transition: "border-color 0.2s ease, box-shadow 0.2s ease",
    backgroundColor: "#f8fafc",
    color: "#1e293b",
    boxSizing: "border-box",
  };

  const labelStyle: React.CSSProperties = {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    fontSize: "13px",
    fontWeight: "600",
    color: "#475569",
    marginBottom: "6px",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
  };

  const handleFocus = (e: React.FocusEvent<HTMLInputElement | HTMLSelectElement>) => {
    e.target.style.borderColor = "#6366f1";
    e.target.style.boxShadow = "0 0 0 3px rgba(99, 102, 241, 0.1)";
    e.target.style.backgroundColor = "#fff";
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement | HTMLSelectElement>) => {
    e.target.style.borderColor = "#e2e8f0";
    e.target.style.boxShadow = "none";
    e.target.style.backgroundColor = "#f8fafc";
  };

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: "rgba(15, 23, 42, 0.4)",
        backdropFilter: "blur(4px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 100,
        animation: "addSiteFadeIn 0.2s ease-out",
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <style>{`
        @keyframes addSiteFadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes addSiteSlideUp {
          from { opacity: 0; transform: translateY(16px) scale(0.97); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
      `}</style>

      <div
        style={{
          background: "white",
          borderRadius: "16px",
          boxShadow: "0 20px 60px rgba(0, 0, 0, 0.15), 0 0 0 1px rgba(0, 0, 0, 0.05)",
          width: "90%",
          maxWidth: "520px",
          maxHeight: "90vh",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          animation: "addSiteSlideUp 0.25s ease-out",
        }}
      >
        {/* Header */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "20px 24px",
            borderBottom: "1px solid #f1f5f9",
            background: "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)",
            color: "white",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <MapPin size={20} />
            <h3 style={{ margin: 0, fontSize: "17px", fontWeight: "600" }}>Ajouter une Unité</h3>
          </div>
          <button
            onClick={onClose}
            style={{
              background: "rgba(255,255,255,0.2)",
              border: "none",
              borderRadius: "8px",
              padding: "6px",
              cursor: "pointer",
              color: "white",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              transition: "background 0.15s",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(255,255,255,0.3)")}
            onMouseLeave={(e) => (e.currentTarget.style.background = "rgba(255,255,255,0.2)")}
          >
            <X size={18} />
          </button>
        </div>

        {/* Content */}
        <div style={{ padding: "24px", overflowY: "auto", flex: 1 }}>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: "16px",
            }}
          >
            {/* Site Name */}
            <div style={{ gridColumn: "1 / -1" }}>
              <label style={labelStyle}>Nom du site *</label>
              <input
                type="text"
                value={newGroup.name}
                onChange={(e) => setNewGroup({ ...newGroup, name: e.target.value })}
                placeholder="Entrer le nom du site"
                style={inputStyle}
                onFocus={handleFocus}
                onBlur={handleBlur}
              />
            </div>

            {/* Société */}
            <div>
              <label style={labelStyle}>Société *</label>
              <select
                value={(newGroup as any).societe || ""}
                onChange={(e) =>
                  setNewGroup({
                    ...newGroup,
                    ...({ societe: Number(e.target.value) } as any),
                  })
                }
                style={{ ...inputStyle, cursor: "pointer", appearance: "auto" }}
                onFocus={handleFocus as any}
                onBlur={handleBlur as any}
              >
                <option value="">Sélectionner Société</option>
                {societes.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Business Unit */}
            <div>
              <label style={labelStyle}>Business Unit *</label>
              <select
                value={(newGroup as any).bu || ""}
                onChange={(e) =>
                  setNewGroup({
                    ...newGroup,
                    ...({ bu: Number(e.target.value) } as any),
                  })
                }
                style={{ ...inputStyle, cursor: "pointer", appearance: "auto" }}
                onFocus={handleFocus as any}
                onBlur={handleBlur as any}
              >
                <option value="">Sélectionner BU</option>
                {bus.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Activité */}
            <div>
              <label style={labelStyle}>Activité *</label>
              <select
                value={(newGroup as any).activite || ""}
                onChange={(e) =>
                  setNewGroup({
                    ...newGroup,
                    ...({ activite: Number(e.target.value) } as any),
                  })
                }
                style={{ ...inputStyle, cursor: "pointer", appearance: "auto" }}
                onFocus={handleFocus as any}
                onBlur={handleBlur as any}
              >
                <option value="">Sélectionner Activité</option>
                {activites.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Filière */}
            <div>
              <label style={labelStyle}>Filière *</label>
              <select
                value={(newGroup as any).filiere || ""}
                onChange={(e) =>
                  setNewGroup({
                    ...newGroup,
                    ...({ filiere: Number(e.target.value) } as any),
                  })
                }
                style={{ ...inputStyle, cursor: "pointer", appearance: "auto" }}
                onFocus={handleFocus as any}
                onBlur={handleBlur as any}
              >
                <option value="">Sélectionner Filière</option>
                {filieres.map((f) => (
                  <option key={f.id} value={f.id}>
                    {f.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Région */}
            <div>
              <label style={labelStyle}>Région *</label>
              <select
                value={(newGroup as any).region || ""}
                onChange={(e) =>
                  setNewGroup({
                    ...newGroup,
                    ...({ region: Number(e.target.value) } as any),
                  })
                }
                style={{ ...inputStyle, cursor: "pointer", appearance: "auto" }}
                onFocus={handleFocus as any}
                onBlur={handleBlur as any}
              >
                <option value="">Sélectionner Région</option>
                {regions.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Type */}
            <div>
              <label style={labelStyle}>Type</label>
              <select
                value={newGroup.type}
                onChange={(e) =>
                  setNewGroup({
                    ...newGroup,
                    type: e.target.value as "Interne" | "Externe",
                  })
                }
                style={{ ...inputStyle, cursor: "pointer", appearance: "auto" }}
                onFocus={handleFocus as any}
                onBlur={handleBlur as any}
              >
                <option value="Interne">Interne</option>
                <option value="Externe">Externe</option>
              </select>
            </div>

            {/* Validation */}
            <div>
              <label style={labelStyle}>Validation</label>
              <label
                htmlFor="add-double-validation"
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  cursor: "pointer",
                  padding: "10px 14px",
                  borderRadius: "10px",
                  backgroundColor: "#f8fafc",
                  border: "2px solid #e2e8f0",
                  transition: "all 0.2s",
                  userSelect: "none",
                  width: "fit-content",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = "#f1f5f9";
                  e.currentTarget.style.borderColor = "#cbd5e1";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = "#f8fafc";
                  e.currentTarget.style.borderColor = "#e2e8f0";
                }}
              >
                <input
                  id="add-double-validation"
                  type="checkbox"
                  checked={(newGroup as any).requireDoubleValidation ?? false}
                  onChange={(e) =>
                    setNewGroup({
                      ...newGroup,
                      ...({
                        requireDoubleValidation: e.target.checked,
                      } as any),
                    })
                  }
                  style={{
                    width: "18px",
                    height: "18px",
                    cursor: "pointer",
                  }}
                />
                <span style={{ fontSize: "14px", fontWeight: 500 }}>Double validation</span>
              </label>
            </div>

            {/* Description */}
            <div style={{ gridColumn: "1 / -1" }}>
              <label style={labelStyle}>Description</label>
              <input
                type="text"
                value={newGroup.description}
                onChange={(e) => setNewGroup({ ...newGroup, description: e.target.value })}
                placeholder="Entrer la description"
                style={inputStyle}
                onFocus={handleFocus}
                onBlur={handleBlur}
              />
            </div>
          </div>
        </div>

        {/* Footer */}
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: "10px",
            padding: "16px 24px",
            borderTop: "1px solid #f1f5f9",
            background: "#f8fafc",
          }}
        >
          <button
            onClick={onClose}
            style={{
              padding: "9px 20px",
              border: "2px solid #e2e8f0",
              borderRadius: "10px",
              background: "white",
              color: "#64748b",
              fontSize: "14px",
              fontWeight: "500",
              cursor: "pointer",
              transition: "all 0.15s",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = "#cbd5e1";
              e.currentTarget.style.color = "#334155";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = "#e2e8f0";
              e.currentTarget.style.color = "#64748b";
            }}
          >
            Annuler
          </button>
          <button
            onClick={onAddGroup}
            style={{
              padding: "9px 24px",
              border: "none",
              borderRadius: "10px",
              background: "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)",
              color: "white",
              fontSize: "14px",
              fontWeight: "600",
              cursor: "pointer",
              transition: "all 0.15s",
              boxShadow: "0 2px 8px rgba(99, 102, 241, 0.3)",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.boxShadow = "0 4px 12px rgba(99, 102, 241, 0.4)";
              e.currentTarget.style.transform = "translateY(-1px)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.boxShadow = "0 2px 8px rgba(99, 102, 241, 0.3)";
              e.currentTarget.style.transform = "translateY(0)";
            }}
          >
            Ajouter
          </button>
        </div>
      </div>
    </div>
  );
}

export default AddSiteGroupDialog;
